/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { GridItem, Heading, VStack } from 'chakra-v3';
import { Text } from './Text';

export interface NumberedFeatureProps {
  number: string;
  title: string;
  description: string;
  colStart?: { base: number; lg: number };
  rowStart?: { base: number; lg: number };
}

/**
 * NumberedFeature Component
 *
 * Displays a numbered feature with title and description.
 * Used in the Built for Precision section.
 *
 * @example
 * ```tsx
 * <NumberedFeature
 *   number="01"
 *   title="Pairwise evaluation"
 *   description="Compare two model responses..."
 *   colStart={{ base: 1, lg: 2 }}
 *   rowStart={{ base: 2, lg: 1 }}
 * />
 * ```
 */
export const NumberedFeature: React.FC<NumberedFeatureProps> = ({ number, title, description, colStart, rowStart }) => {
  return (
    <GridItem colStart={colStart} rowStart={rowStart}>
      <VStack gap={{ base: 4, lg: '20px' }} alignItems="flex-start">
        <Text
          variant="inherit"
          fontSize={{ base: '40px', lg: '48px' }}
          fontWeight="200"
          color="text.accent"
          fontFamily="body"
          lineHeight="1.8"
          h={{ lg: '60px' }}
        >
          {number}
        </Text>
        <VStack gap={{ base: 2, lg: '8px' }} alignItems="flex-start">
          <Heading
            as="h3"
            fontSize={{ base: '24px', lg: '32px' }}
            fontWeight="normal"
            color="text.dark"
            fontFamily="body"
            lineHeight="1.4"
            letterSpacing="0.32px"
          >
            {title}
          </Heading>
          <Text size={{ base: 'sm', lg: 'md' }} color="text.darkTeal">
            {description}
          </Text>
        </VStack>
      </VStack>
    </GridItem>
  );
};
